import { LANGUAGES, LANGUAGE_CODES, type JobInput, type JobRequirements, type LanguageCode, type RoleCategoryId, type RoleSummary } from '@eduvy/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon } from '../components/icons';
import { Badge, Button, Callout, Card, Chip, ErrorNote, Field, Input, Loading, NumberField, Options, Section, Segmented, Skeleton, Spinner, Stepper, Textarea, cx } from '../components/ui';
import { api } from '../lib/api';
import { draft } from '../lib/drafts';

interface Catalog {
  categories: Array<{ id: RoleCategoryId; label: string }>;
  roles: RoleSummary[];
}

interface RolePreview extends RoleSummary {
  experience: { min: number; max: number };
  shifts: boolean;
  competencies: Array<{ label: string; weight: number; description: string }>;
  facts: string[];
  tasks: Array<{ kind: 'roleplay' | 'written' | 'hands_on'; title: string }>;
}

interface Defaults {
  companyName: string;
  interviewerName: string;
  languages: LanguageCode[];
  location: string;
  workMode: string;
}

/** What the review page (the job editor) receives. */
export interface JobDraftState {
  draft: JobInput;
  source: 'ai' | 'template';
  roleTitle: string | null;
}

/** The form as typed: numbers stay strings until they are sent. */
interface Form {
  title: string;
  companyName: string;
  interviewerName: string;
  aboutCompany: string;
  location: string;
  workMode: JobRequirements['workMode'];
  expMin: string;
  expMax: string;
  budget: string;
  notice: string;
  shifts: 'role' | 'yes' | 'no';
  languages: LanguageCode[];
  mustHaves: string;
  jobDescription: string;
}

const STORE = 'eduvy:new-job';
const CUSTOM = 'custom';
const STEPS = [
  { label: 'Role', hint: 'from the library, or your own' },
  { label: 'Requirements', hint: 'what you need' },
  { label: 'Review', hint: 'the interview and the assessment' },
];

function remember(value: unknown): void {
  try {
    sessionStorage.setItem(STORE, JSON.stringify(value));
  } catch {
    /* private mode: the form is just not restored */
  }
}

function recall(): { roleId: string | null; form: Form } | null {
  try {
    const raw = sessionStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as { roleId: string | null; form: Form }) : null;
  } catch {
    return null;
  }
}

const num = (s: string) => (s.trim() === '' ? undefined : Number(s));

function toRequirements(roleId: string | null, f: Form): JobRequirements {
  const req: JobRequirements = {
    title: f.title.trim(),
    companyName: f.companyName.trim(),
    location: f.location.trim(),
    workMode: f.workMode,
    experience: { min: num(f.expMin) ?? 0, max: num(f.expMax) ?? 10 },
    languages: f.languages.length ? f.languages : ['en-IN'],
  };
  if (roleId && roleId !== CUSTOM) req.roleId = roleId;
  if (f.interviewerName.trim()) req.interviewerName = f.interviewerName.trim();
  if (f.aboutCompany.trim()) req.aboutCompany = f.aboutCompany.trim();
  if (num(f.budget) !== undefined) req.budgetMaxLpa = num(f.budget)!;
  if (num(f.notice) !== undefined) req.maxNoticeDays = Math.round(num(f.notice)!);
  if (f.shifts !== 'role') req.shifts = f.shifts === 'yes';
  if (f.mustHaves.trim()) req.mustHaves = f.mustHaves.trim();
  if (f.jobDescription.trim()) req.jobDescription = f.jobDescription.trim();
  return req;
}

/** Why the form cannot be sent yet, or null. */
function problem(f: Form): string | null {
  if (f.title.trim().length < 2) return 'Give the job a title.';
  if (!f.companyName.trim()) return 'Add your company name.';
  if (f.location.trim().length < 2) return 'Add the job location.';
  const lo = num(f.expMin) ?? 0;
  const hi = num(f.expMax) ?? 10;
  if (Number.isNaN(lo) || Number.isNaN(hi) || lo < 0 || hi < lo) return 'Check the experience range.';
  if ([f.budget, f.notice].some((v) => v.trim() !== '' && Number.isNaN(Number(v)))) return 'Budget and notice period must be numbers.';
  return null;
}

/**
 * A new job in three steps: pick the role from the library (or describe one), give the company's
 * requirements, then review what the AI drafted (or the template) in the job editor before saving.
 */
export function NewJobPage() {
  const navigate = useNavigate();
  const catalog = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Catalog>('/api/roles'), staleTime: Infinity });
  const defaults = useQuery({ queryKey: ['job-defaults'], queryFn: () => api.get<Defaults>('/api/jobs/defaults') });
  const saved = useMemo(recall, []);
  const [roleId, setRoleId] = useState<string | null>(saved?.roleId ?? null);
  const [form, setForm] = useState<Form | null>(saved?.form ?? null);
  const [step, setStep] = useState<'role' | 'details'>(saved?.form ? 'details' : 'role');

  // First visit: the company's details from its latest job.
  useEffect(() => {
    if (form || !defaults.data) return;
    const d = defaults.data;
    setForm({
      title: '',
      companyName: d.companyName,
      interviewerName: d.interviewerName,
      aboutCompany: '',
      location: d.location,
      workMode: (['On-site', 'Hybrid', 'Remote'].includes(d.workMode) ? d.workMode : 'On-site') as Form['workMode'],
      expMin: '',
      expMax: '',
      budget: '',
      notice: '',
      shifts: 'role',
      languages: d.languages.length ? d.languages : ['en-IN'],
      mustHaves: '',
      jobDescription: '',
    });
  }, [defaults.data, form]);

  useEffect(() => {
    if (form) remember({ roleId, form });
  }, [roleId, form]);

  if (catalog.isLoading || !form) return catalog.error || defaults.error ? <ErrorNote error={catalog.error ?? defaults.error} /> : <Loading />;
  const roles = catalog.data?.roles ?? [];
  const role = roles.find((r) => r.id === roleId) ?? null;

  const choose = (id: string) => {
    const r = roles.find((x) => x.id === id);
    setRoleId(id);
    // A different role starts from its own title and experience range; the same one keeps what was typed.
    if (id !== roleId) setForm({ ...form, title: r ? r.title : '', expMin: '', expMax: '' });
    setStep('details');
    window.scrollTo({ top: 0 });
  };

  const open = (state: JobDraftState) => {
    try {
      sessionStorage.removeItem(STORE);
    } catch {
      /* ignore */
    }
    navigate('/jobs/new/review', { state });
  };

  return (
    <>
      <div className="mb-8">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <Link to="/jobs" className="inline-flex items-center gap-1 text-sm font-medium text-muted-fg transition hover:text-accent">
            <Icon name="arrowLeft" className="h-4 w-4" />
            Jobs
          </Link>
          <Stepper steps={STEPS} current={step === 'role' ? 0 : 1} onStep={() => setStep('role')} />
        </div>
        <p className="eyebrow">New job</p>
        <h1 className="display mt-2 text-[32px] font-medium leading-[1.1] text-fg sm:text-[38px]">{step === 'role' ? 'What role are you hiring for?' : role ? role.title : 'Describe the role'}</h1>
        <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-muted-fg">
          {step === 'role'
            ? 'Every role comes with an AI interview rubric and a practical skills assessment. Tell us your requirements next, and the AI tailors them to your company.'
            : 'The AI reads your requirements (and your job description, if you paste it) and drafts the interview and the skills assessment for you to review.'}
        </p>
      </div>
      {step === 'role' ? <RolePicker catalog={catalog.data!} onChoose={choose} /> : <Details role={role} form={form} setForm={setForm} onDone={open} />}
    </>
  );
}

// ---- step 1: the role library --------------------------------------------------------------

function RolePicker({ catalog, onChoose }: { catalog: Catalog; onChoose: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<RoleCategoryId | 'all'>('all');
  const search = q.trim().toLowerCase();
  const label = (id: RoleCategoryId) => catalog.categories.find((c) => c.id === id)?.label ?? id;
  const matches = catalog.roles.filter(
    (r) =>
      (category === 'all' || r.category === category) &&
      (!search || [r.title, r.summary, r.department, label(r.category), ...r.aliases].some((t) => t.toLowerCase().includes(search))),
  );
  const groups = catalog.categories.map((c) => ({ ...c, roles: matches.filter((r) => r.category === c.id) })).filter((g) => g.roles.length);
  const countIn = (id: RoleCategoryId | 'all') => catalog.roles.filter((r) => id === 'all' || r.category === id).length;

  return (
    <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
      {/* Category rail */}
      {/* min-w-0: the chip row's unwrapped width must not widen the page on phones. */}
      <aside className="min-w-0 lg:sticky lg:top-24 lg:self-start">
        <p className="eyebrow mb-2 hidden lg:block">Categories</p>
        <div className="scrollbar-thin flex gap-1.5 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
          {[{ id: 'all' as const, label: 'All roles' }, ...catalog.categories].map((c) => {
            const on = category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={cx(
                  'flex shrink-0 items-center justify-between gap-3 rounded-sm px-3 py-2 text-left text-[13px] font-medium transition',
                  on ? 'bg-surface text-fg shadow-card ring-1 ring-border dark:bg-white/8' : 'text-muted-fg hover:bg-surface/70 hover:text-fg dark:hover:bg-white/5',
                )}
              >
                <span className="truncate">{c.label}</span>
                <span className={cx('font-mono text-[10px]', on ? 'text-accent' : 'text-faint')}>{countIn(c.id)}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <div className="min-w-0 space-y-6">
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <Input
            autoFocus
            big
            type="search"
            leading={<Icon name="search" className="h-5 w-5" />}
            placeholder={`Search ${catalog.roles.length} roles: accountant, telecaller, nurse, developer…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search roles"
          />
          <button
            type="button"
            onClick={() => onChoose(CUSTOM)}
            className="group relative flex items-center gap-3 overflow-hidden rounded-md bg-night px-4 py-3 text-left text-white ring-1 ring-white/10 transition hover:ring-white/30"
          >
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-brand-gradient shadow-brand">
              <Icon name="wand" className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold">Describe your own role</span>
              <span className="block text-xs text-white/60">The AI designs the interview and assessment</span>
            </span>
            <Icon name="arrowRight" className="ml-2 h-4 w-4 shrink-0 text-white/60 transition group-hover:translate-x-0.5 group-hover:text-white" />
          </button>
        </div>

        {groups.length === 0 && (
          <Callout tone="brand" icon="wand" action={<Button size="sm" variant="primary" onClick={() => onChoose(CUSTOM)}>Describe it</Button>}>
            No role matches “{q}”. Describe your own role instead: the AI designs it from your requirements.
          </Callout>
        )}
        {groups.map((g) => (
          <section key={g.id}>
            <div className="mb-3 flex items-baseline gap-2">
              <h2 className="text-sm font-semibold text-fg">{g.label}</h2>
              <span className="font-mono text-[11px] text-faint">{g.roles.length}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
              {g.roles.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onChoose(r.id)}
                  className="surface surface-hover group flex flex-col rounded-lg p-4 text-left"
                >
                  <span className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-fg transition group-hover:text-accent">{r.title}</span>
                    <Icon name="arrowRight" className="mt-0.5 h-4 w-4 shrink-0 text-faint opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100 group-hover:text-accent" />
                  </span>
                  <span className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-muted-fg">{r.summary}</span>
                  {r.customerFacing && (
                    <span className="mt-3">
                      <Badge tone="violet" size="xs">
                        customer-facing
                      </Badge>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

// ---- step 2: the company's requirements ------------------------------------------------------

function Details({ role, form, setForm, onDone }: { role: RoleSummary | null; form: Form; setForm: (f: Form) => void; onDone: (s: JobDraftState) => void }) {
  const preview = useQuery({ queryKey: ['role', role?.id], queryFn: () => api.get<RolePreview>(`/api/roles/${role!.id}`), enabled: Boolean(role), staleTime: Infinity });
  const [busy, setBusy] = useState<'ai' | 'template' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [aiFailed, setAiFailed] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm({ ...form, [k]: v });
  const exp = preview.data?.experience;
  const invalid = problem(form);

  useEffect(() => () => abort.current?.abort(), []);

  const run = async (how: 'ai' | 'template') => {
    if (invalid) return setError(invalid);
    setBusy(how);
    setError(null);
    setAiFailed(false);
    const req = toRequirements(role?.id ?? null, {
      ...form,
      expMin: form.expMin || String(exp?.min ?? 0),
      expMax: form.expMax || String(exp?.max ?? 10),
    });
    try {
      if (how === 'template') {
        const job = await api.post<JobInput>('/api/jobs/build', req);
        onDone({ draft: job, source: 'template', roleTitle: role?.title ?? null });
      } else {
        abort.current = new AbortController();
        const { job } = await draft<{ job: JobInput }>('/api/jobs/draft', req, abort.current.signal);
        onDone({ draft: job, source: 'ai', roleTitle: role?.title ?? null });
      }
    } catch (err) {
      setError(err);
      if (how === 'ai') setAiFailed(true);
    } finally {
      setBusy(null);
    }
  };

  const expValue = (s: string, fallback: number) => (s === '' ? fallback : Number(s));

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="min-w-0 space-y-5">
        <Section id="job" n={1} title="The job" description="Where it is, how much experience, and what matters most.">
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Job title" className="md:col-span-2">
              <Input value={form.title} placeholder={role ? role.title : 'e.g. Solar Panel Installer'} onChange={(e) => set('title', e.target.value)} big />
            </Field>
            <Field label="Location">
              <Input value={form.location} placeholder="City" leading={<Icon name="pin" className="h-4 w-4" />} onChange={(e) => set('location', e.target.value)} />
            </Field>
            <Field label="Work mode" as="div">
              <Options
                label="Work mode"
                value={form.workMode}
                onChange={(v) => set('workMode', v)}
                compact
                columns={3}
                options={[
                  { value: 'On-site', label: 'On-site' },
                  { value: 'Hybrid', label: 'Hybrid' },
                  { value: 'Remote', label: 'Remote' },
                ]}
              />
            </Field>
            <Field label="Experience, from" hint={exp ? `The ${role?.title} template suggests ${exp.min} to ${exp.max} years.` : undefined}>
              <NumberField value={expValue(form.expMin, exp?.min ?? 0)} min={0} max={40} step={0.5} suffix="years" onChange={(v) => set('expMin', String(v))} aria-label="Minimum experience" />
            </Field>
            <Field label="Experience, up to">
              <NumberField value={expValue(form.expMax, exp?.max ?? 10)} min={0} max={40} step={0.5} suffix="years" onChange={(v) => set('expMax', String(v))} aria-label="Maximum experience" />
            </Field>
            <Field label="Salary budget, up to" optional hint="Kept private: the AI never tells candidates. Higher expectations go to review.">
              <Input type="number" min={0} step={0.5} value={form.budget} placeholder="e.g. 4.5" trailing={<span className="text-xs">LPA</span>} onChange={(e) => set('budget', e.target.value)} />
            </Field>
            <Field label="Longest notice period" optional hint="Longer notice periods go to review.">
              <Input type="number" min={0} value={form.notice} placeholder="e.g. 30" trailing={<span className="text-xs">days</span>} onChange={(e) => set('notice', e.target.value)} />
            </Field>
            <Field label="Rotational shifts or weekends" className="md:col-span-2" as="div">
              <Segmented
                label="Shifts"
                size="md"
                value={form.shifts}
                onChange={(v) => set('shifts', v)}
                options={[
                  { value: 'role', label: role ? `Usual for the role (${preview.data?.shifts ? 'yes' : 'no'})` : 'Let the AI decide' },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
              />
            </Field>
            <Field label="What you need most" hint="Skills, tools, qualifications or licences. The interviewer asks about them." className="md:col-span-2">
              <Textarea rows={3} value={form.mustHaves} placeholder="e.g. Tally Prime and GST returns; fluent Tamil; a two-wheeler" onChange={(e) => set('mustHaves', e.target.value)} />
            </Field>
            <Field label="Your job description" optional hint="Paste it and the AI tailors the questions and tasks to it." className="md:col-span-2">
              <Textarea rows={7} value={form.jobDescription} placeholder="Paste the job description here" onChange={(e) => set('jobDescription', e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section id="company" n={2} title="Your company" description="How the interviewer introduces itself, and which languages candidates can use.">
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Company name" hint="As the AI says it.">
              <Input value={form.companyName} leading={<Icon name="building" className="h-4 w-4" />} onChange={(e) => set('companyName', e.target.value)} />
            </Field>
            <Field label="Interviewer's name">
              <Input value={form.interviewerName} leading={<Icon name="user" className="h-4 w-4" />} onChange={(e) => set('interviewerName', e.target.value)} />
            </Field>
            <Field label="About your company" optional hint="A line or two the interviewer can share with candidates." className="md:col-span-2">
              <Textarea rows={2} value={form.aboutCompany} onChange={(e) => set('aboutCompany', e.target.value)} />
            </Field>
            <Field label="Languages candidates can use in the interview" className="md:col-span-2" as="div">
              <div className="flex flex-wrap gap-2">
                {LANGUAGE_CODES.map((code) => {
                  const on = form.languages.includes(code);
                  return (
                    <Chip
                      key={code}
                      on={on}
                      onClick={() => {
                        const next = on ? form.languages.filter((l) => l !== code) : [...form.languages, code];
                        if (next.length) set('languages', next);
                      }}
                    >
                      {LANGUAGES[code].label}
                    </Chip>
                  );
                })}
              </div>
            </Field>
          </div>
        </Section>
      </div>

      <aside className="min-w-0 space-y-4 lg:sticky lg:top-24 lg:self-start">
        {busy === 'ai' ? (
          <DraftingCard onCancel={() => abort.current?.abort()} />
        ) : (
          <Card tone="night" padding="md">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] bg-brand-gradient shadow-brand">
                <Icon name="sparkles" className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold">Ready when you are</p>
                <p className="text-xs text-white/60">Nothing reaches candidates until you save and activate the job.</p>
              </div>
            </div>
            {error ? (
              <p className="mt-4 rounded-[9px] bg-white/10 px-3 py-2 text-xs text-[#fda4b4]" role="alert">
                {error instanceof Error ? error.message : String(error)}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col gap-2">
              <Button variant="primary" size="lg" icon="sparkles" onClick={() => void run('ai')} disabled={busy !== null} className="w-full">
                {aiFailed ? 'Try the AI draft again' : 'Draft with AI'}
              </Button>
              <Button size="md" loading={busy === 'template'} onClick={() => void run('template')} disabled={busy !== null} className="w-full !bg-white/10 !text-white !ring-white/15 hover:!bg-white/15">
                {role ? 'Use the template as it is' : 'Start from a generic template'}
              </Button>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-white/50">The AI draft takes 20 to 60 seconds and rewrites the rubric and tasks for your company. The template is instant.</p>
          </Card>
        )}

        <Card title={role ? 'What the template brings' : 'What the AI designs'} description={role ? 'Rewritten for your company with “Draft with AI”.' : undefined}>
          {!role ? (
            <ul className="space-y-3 text-sm text-muted-fg">
              {[
                ['target', '4-6 competencies the AI interviewer probes and scores, with scenario questions'],
                ['clipboard', 'The facts to establish and your rules (budget, notice, experience)'],
                ['users', 'A skills assessment: role-plays where the AI plays the people this job deals with, and written tasks'],
              ].map(([icon, text]) => (
                <li key={text} className="flex gap-3">
                  <Icon name={icon as 'target'} className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          ) : preview.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : preview.data ? (
            <div className="space-y-5 text-sm">
              <p className="leading-relaxed text-muted-fg">{preview.data.summary}</p>
              <div>
                <p className="eyebrow mb-2">Interview competencies</p>
                <ul className="space-y-2">
                  {preview.data.competencies.map((c) => (
                    <li key={c.label}>
                      <div className="flex justify-between gap-3 text-[13px]">
                        <span className="text-fg">{c.label}</span>
                        <span className="font-mono text-xs text-muted-fg">{c.weight}%</span>
                      </div>
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-brand-gradient" style={{ width: `${c.weight}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="eyebrow mb-2">Skills assessment tasks</p>
                <ul className="space-y-1.5">
                  {preview.data.tasks.map((t) => (
                    <li key={t.title} className="flex items-start gap-2 text-[13px]">
                      <Badge tone={t.kind === 'roleplay' ? 'violet' : 'blue'} size="xs" className="mt-0.5 shrink-0">
                        {t.kind === 'roleplay' ? 'role-play' : 'written'}
                      </Badge>
                      <span className="text-fg-2">{t.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <ErrorNote error={preview.error} />
          )}
        </Card>
      </aside>
    </div>
  );
}

const STAGES = [
  { at: 0, label: 'Reading your requirements' },
  { at: 8, label: 'Writing the interview rubric' },
  { at: 25, label: 'Writing the skills-assessment tasks' },
  { at: 50, label: 'Checking and tidying the draft' },
];

function DraftingCard({ onCancel }: { onCancel: () => void }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const current = STAGES.reduce((i, s, idx) => (seconds >= s.at ? idx : i), 0);
  return (
    <Card tone="night" padding="md">
      <div className="flex items-center gap-3" role="status">
        <span className="orb h-10 w-10 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Drafting your job</p>
          <p className="font-mono text-[11px] text-white/55">{seconds}s · usually 20 to 60 seconds</p>
        </div>
      </div>
      <ol className="mt-5 space-y-2.5">
        {STAGES.map((s, i) => {
          const state = i < current ? 'done' : i === current ? 'now' : 'next';
          return (
            <li key={s.label} className={cx('flex items-center gap-3 text-sm transition', state === 'next' ? 'text-white/35' : 'text-white')}>
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                {state === 'done' ? (
                  <Icon name="check" className="h-4 w-4 text-[#4ade80]" strokeWidth={2.5} />
                ) : state === 'now' ? (
                  <Spinner className="h-4 w-4 text-[#f7a6cf]" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                )}
              </span>
              {s.label}
            </li>
          );
        })}
      </ol>
      <Button size="sm" onClick={onCancel} className="mt-5 w-full !bg-white/10 !text-white !ring-white/15 hover:!bg-white/15">
        Cancel
      </Button>
    </Card>
  );
}
